import React from 'react'
import {ActivityIndicator, StyleSheet, View} from 'react-native'
import {
  AppBskyEmbedImages,
  AppBskyEmbedRecord,
  AppBskyEmbedRecordWithMedia,
  AppBskyEmbedVideo,
} from '@atproto/api'
import {Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useQuery} from '@tanstack/react-query'

import {TapApiClient} from '#/lib/api/tap'
import {usePalette} from '#/lib/hooks/usePalette'
import {makeProfileLink} from '#/lib/routes/links'
import {sanitizeDisplayName} from '#/lib/strings/display-names'
import {sanitizeHandle} from '#/lib/strings/handles'
import {usePostFeedQuery} from '#/state/queries/post-feed'
import {Link} from '#/view/com/util/Link'
import {PostEmbeds, PostEmbedViewContext} from '#/view/com/util/post-embeds'
import {Text} from '#/view/com/util/text/Text'
import {PreviewableUserAvatar} from '#/view/com/util/UserAvatar'
// import {DesktopSearch} from '#/view/shell/desktop/Search'
import {useTheme} from '#/alf'
import * as Layout from '#/components/Layout'

const FEED_URI =
  'at://did:plc:rvm7yxk6go3rqceh2t7svoct/app.bsky.feed.generator/aaahjvrm3fhdu'

export function HeroSectionPage() {
  const feedDesc = `feedgen|${FEED_URI}` as const
  const {data, isLoading, error} = usePostFeedQuery(feedDesc)
  const pal = usePalette('default')
  const _t = useTheme()
  const {_} = useLingui()

  // TAP API fallback: only fires when Bluesky feed fails
  const {data: tapData} = useQuery({
    queryKey: ['hero-tap-fallback'],
    queryFn: async () => {
      const client = new TapApiClient()
      return client.getVideoFeed({limit: 5})
    },
    enabled: !!error,
  })

  const tapFallbackPost = React.useMemo(() => {
    if (!tapData?.posts) return undefined
    for (const post of tapData.posts) {
      if (
        post.embed &&
        (AppBskyEmbedImages.isView(post.embed) ||
          AppBskyEmbedVideo.isView(post.embed) ||
          AppBskyEmbedRecordWithMedia.isView(post.embed))
      ) {
        const emptyModui = {
          noOverride: false,
          filters: [],
          blurs: [],
          alerts: [],
          informs: [],
          filter: false,
          blur: false,
          alert: false,
          inform: false,
        }
        return {post, moderation: {ui: () => emptyModui}} as any
      }
    }
    return undefined
  }, [tapData])

  // Extract the most recent post that meets all criteria
  const latestPost = React.useMemo(() => {
    if (!data?.pages) return undefined

    // Loop through all pages and slices to find the first qualifying post
    for (const page of data.pages) {
      for (const slice of page.slices) {
        for (const item of slice.items) {
          // Skip if it's a repost (has a reason)
          if (slice.reason) continue

          // Skip if it's a quote post (has a record embed)
          if (item.post.embed && AppBskyEmbedRecord.isView(item.post.embed)) {
            continue
          }

          // Skip if it's a reply to another post
          if (item.record.reply) {
            continue
          }

          // Skip if post doesn't have media (images or video)
          const hasMedia =
            (item.post.embed && AppBskyEmbedImages.isView(item.post.embed)) ||
            (item.post.embed && AppBskyEmbedVideo.isView(item.post.embed)) ||
            (item.post.embed &&
              AppBskyEmbedRecordWithMedia.isView(item.post.embed))

          if (!hasMedia) {
            continue
          }

          // Skip if the author has opted out of unauthenticated viewing
          const hasPwiOptOut = item.post.author.labels?.some(
            label => label.val === '!no-unauthenticated',
          )
          if (hasPwiOptOut) {
            continue
          }

          // This is an original post with media, return it
          return item
        }
      }
    }

    return undefined
  }, [data])

  return (
    <Layout.Screen>
      <Layout.Center>
        <View style={{paddingLeft: 24}}>
          <div className="flex flex-col items-center justify-center py-16 gap-8 max-w-2xl w-full px-4">
            <div className="text-center w-full">
              <div className="inline-flex h-7 items-center justify-center rounded-full border border-white/5 bg-white/10 px-3 text-xs text-white dark:text-black transition-all ease-in hover:cursor-pointer hover:bg-white/20 gap-1 mb-6" />
              <h1
                style={{
                  fontSize: 32,
                  color: _t.name === 'light' ? 'black' : 'white',
                  fontWeight: '800',
                  textAlign: 'left',
                  lineHeight: 1.2,
                  marginLeft: 15,
                  marginBottom: 0,
                }}>
                <Text
                  style={{
                    fontSize: 32,
                    color: _t.name === 'light' ? 'black' : 'white',
                    fontWeight: '800',
                  }}>
                  See what your bate bros
                </Text>
                <br />
                <Text
                  style={{
                    fontSize: 32,
                    color: _t.name === 'light' ? 'black' : 'white',
                    fontWeight: '800',
                  }}>
                  are posting on Bluesky
                </Text>
              </h1>
            </div>

            {/* Latest Post from Feed */}
            {isLoading ? (
              <View style={styles.postCard}>
                <ActivityIndicator size="large" color="#FD7C21" />
              </View>
            ) : error && tapFallbackPost ? (
              <LatestPostCard post={tapFallbackPost} />
            ) : error ? (
              <View style={styles.postCard}>
                <Text style={pal.textLight}>
                  <Trans>Unable to load latest post</Trans>
                </Text>
              </View>
            ) : latestPost ? (
              <LatestPostCard post={latestPost} />
            ) : null}

            {/* Search Bar */}
            {/* <View style={{width: '100%', maxWidth: 500, marginLeft: 15, marginTop: 48}}>
              <Text
                style={{
                  fontSize: 20,
                  color: 'white',
                  fontWeight: '700',
                  marginBottom: 12,
                }}>
                Find your bros
              </Text>
              <DesktopSearch />
            </View> */}
          </div>
        </View>
      </Layout.Center>
    </Layout.Screen>
  )
}

function LatestPostCard({post}: {post: any}) {
  const pal = usePalette('default')
  const t = useTheme()
  const {_} = useLingui()

  const authorHref = makeProfileLink(post.post.author)
  const postHref = makeProfileLink(
    post.post.author,
    'post',
    post.post.uri.split('/').pop(),
  )

  return (
    <View
      style={[styles.postCard, {backgroundColor: t.atoms.bg.backgroundColor}]}>
      {post.post.embed ? (
        <Link href={postHref}>
          <PostEmbeds
            embed={post.post.embed}
            moderation={post.moderation}
            viewContext={PostEmbedViewContext.Feed}
          />
        </Link>
      ) : null}

      <View style={styles.postHeader}>
        <PreviewableUserAvatar
          size={42}
          profile={post.post.author}
          moderation={post.moderation.ui('avatar')}
          type={post.post.author.associated?.labeler ? 'labeler' : 'user'}
        />
        <Link href={authorHref} style={styles.authorInfo}>
          <Text type="lg-bold" style={pal.text} numberOfLines={1}>
            {sanitizeDisplayName(
              post.post.author.displayName ||
                sanitizeHandle(post.post.author.handle),
            )}
          </Text>
          <Text type="md" style={pal.textLight} numberOfLines={1}>
            @{post.post.author.handle}
          </Text>
        </Link>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  postCard: {
    width: '100%',
    maxWidth: 600,
    borderRadius: 12,
    padding: 16,
    marginBottom: 8,
  },
  postHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 12,
  },
  authorInfo: {
    flexDirection: 'column',
    gap: 2,
    flex: 1,
  },
})
